// Generic numeric-range facet — used for derived.age and
// derived.age_at_diagnosis. Supports four modes:
//   - "between" — emits a single FilterRule with op ">=" and a paired "<="
//   - "at least" — single ">=" rule
//   - "at most"  — single "<=" rule
//   - "exactly"  — single "=" rule
//
// Why two `>=` / `<=` rules instead of `BETWEEN`? The backend filter engine
// supports BETWEEN, but using a pair of >= / <= rules keeps the FilterRail's
// "one rule per (field, op) signature" mental model simple, plays nicely
// with the existing ActiveFilterChips renderer, and round-trips cleanly to
// the planner's whitelisted op set.
import { useEffect, useState } from "react";
import { useExplorerStore } from "../../store/explorerStore";
import type { FilterGroup, FilterRule } from "../../types/inpact";
import { isGroup } from "../../lib/filterLogic";

type Mode = "between" | "atLeast" | "atMost" | "exactly";

/** Order-independent signature of a field's rules, for change detection. */
function sigOf(rules: FilterRule[]): string {
  return rules.map((r) => `${r.op}:${r.value}`).sort().join("|");
}

interface Props {
  field: string;     // e.g. "derived.age"
  ariaLabel: string; // e.g. "Age"
  disabled?: boolean; // greyed-out AND non-interactive (e.g. child facets when Has-child=No)
}

function rulesForField(group: FilterGroup, field: string): FilterRule[] {
  return group.rules.filter(
    (r): r is FilterRule => !isGroup(r) && r.field === field,
  );
}

function setRulesForField(
  group: FilterGroup,
  field: string,
  newRules: FilterRule[],
): FilterGroup {
  const others = group.rules.filter(
    (r) => isGroup(r) || (r as FilterRule).field !== field,
  );
  return { ...group, rules: [...others, ...newRules] };
}

function deriveModeFromRules(rules: FilterRule[]): {
  mode: Mode;
  min: string;
  max: string;
} {
  if (rules.length === 0) return { mode: "between", min: "", max: "" };
  const ge = rules.find((r) => r.op === ">=");
  const le = rules.find((r) => r.op === "<=");
  const eq = rules.find((r) => r.op === "=");
  if (eq) return { mode: "exactly", min: String(eq.value ?? ""), max: "" };
  if (ge && le) {
    return {
      mode: "between",
      min: String(ge.value ?? ""),
      max: String(le.value ?? ""),
    };
  }
  if (ge) return { mode: "atLeast", min: String(ge.value ?? ""), max: "" };
  if (le) return { mode: "atMost", min: "", max: String(le.value ?? "") };
  return { mode: "between", min: "", max: "" };
}

function buildRules(
  field: string,
  mode: Mode,
  min: string,
  max: string,
): FilterRule[] {
  const minNum = min === "" ? null : Number(min);
  const maxNum = max === "" ? null : Number(max);
  const numericMin = minNum !== null && Number.isFinite(minNum) ? minNum : null;
  const numericMax = maxNum !== null && Number.isFinite(maxNum) ? maxNum : null;

  if (mode === "exactly") {
    if (numericMin === null) return [];
    return [{ field, op: "=", value: numericMin }];
  }
  if (mode === "atLeast") {
    if (numericMin === null) return [];
    return [{ field, op: ">=", value: numericMin }];
  }
  if (mode === "atMost") {
    if (numericMax === null) return [];
    return [{ field, op: "<=", value: numericMax }];
  }
  // between
  const rules: FilterRule[] = [];
  if (numericMin !== null) rules.push({ field, op: ">=", value: numericMin });
  if (numericMax !== null) rules.push({ field, op: "<=", value: numericMax });
  return rules;
}

export function NumericRangeFacet({ field, ariaLabel, disabled = false }: Props) {
  const { filterGroup, setFilterGroup } = useExplorerStore();
  const existing = rulesForField(filterGroup, field);
  const initial = deriveModeFromRules(existing);

  const [mode, setMode] = useState<Mode>(initial.mode);
  const [min, setMin] = useState<string>(initial.min);
  const [max, setMax] = useState<string>(initial.max);

  // Resync from the store ONLY on external changes (reset-all, chip removal,
  // AnaMoni). We compare the store's rule signature to the one our local state
  // would emit: during local edits they match (commit already pushed our state
  // to the store), so this is a no-op and the mid-edit mode is preserved —
  // "between" with only a min entered stays "between" instead of collapsing to
  // "at least". They diverge only when something outside this facet changed the
  // field, which is exactly when a stale local value would otherwise resurrect
  // a just-removed filter.
  const storeSig = sigOf(existing);
  const localSig = sigOf(buildRules(field, mode, min, max));
  useEffect(() => {
    if (storeSig !== localSig) {
      const next = deriveModeFromRules(rulesForField(filterGroup, field));
      setMode(next.mode);
      setMin(next.min);
      setMax(next.max);
    }
    // Keyed on storeSig only: external edits change it; local edits leave it
    // equal to localSig (checked inside) so we don't fight the user's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSig]);

  const commit = (nextMode: Mode, nextMin: string, nextMax: string) => {
    const rules = buildRules(field, nextMode, nextMin, nextMax);
    setFilterGroup(setRulesForField(filterGroup, field, rules));
  };

  const onModeChange = (m: Mode) => {
    setMode(m);
    commit(m, min, max);
  };
  const onMinChange = (v: string) => {
    setMin(v);
    commit(mode, v, max);
  };
  const onMaxChange = (v: string) => {
    setMax(v);
    commit(mode, min, v);
  };

  return (
    // <fieldset disabled> makes every control inside non-interactive in one
    // shot, so a disabled child facet can't be edited (the CSS class still
    // greys it). minInlineSize:auto undoes the fieldset flex-min-width quirk.
    <fieldset
      className="numeric-range"
      aria-label={ariaLabel}
      disabled={disabled}
      style={{ border: 0, margin: 0, padding: 0, minInlineSize: "auto" }}
    >
      <label className="numeric-range__mode">
        <span className="visually-hidden">Comparison mode</span>
        <select
          value={mode}
          onChange={(e) => onModeChange(e.target.value as Mode)}
          aria-label={`${ariaLabel} comparison mode`}
        >
          <option value="between">between</option>
          <option value="atLeast">at least</option>
          <option value="atMost">at most</option>
          <option value="exactly">exactly</option>
        </select>
      </label>
      <div className="numeric-range__inputs">
        {mode === "between" && (
          <>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="min"
              aria-label={`${ariaLabel} minimum`}
              value={min}
              onChange={(e) => onMinChange(e.target.value)}
            />
            <span className="numeric-range__sep" aria-hidden="true">–</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="max"
              aria-label={`${ariaLabel} maximum`}
              value={max}
              onChange={(e) => onMaxChange(e.target.value)}
            />
          </>
        )}
        {mode === "atLeast" && (
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="min"
            aria-label={`${ariaLabel} minimum`}
            value={min}
            onChange={(e) => onMinChange(e.target.value)}
          />
        )}
        {mode === "atMost" && (
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="max"
            aria-label={`${ariaLabel} maximum`}
            value={max}
            onChange={(e) => onMaxChange(e.target.value)}
          />
        )}
        {mode === "exactly" && (
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="value"
            aria-label={`${ariaLabel} value`}
            value={min}
            onChange={(e) => onMinChange(e.target.value)}
          />
        )}
      </div>
    </fieldset>
  );
}
